import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus, MapPin, CircleAlert } from "lucide-react";
import { Pagina, Cartao, Carregando, Falha } from "../shell/Pagina.js";
import { EmptyState } from "@pessoas/ui";
import { trpc } from "../lib/trpc.js";
import { cnpj as fCnpj } from "../lib/formato.js";
import { UFS } from "../lib/br.js";

/**
 * Configurações: empresa e estabelecimentos.
 *
 * É o passo 1 da implantação, e a tela existe porque o vínculo EXIGE um
 * estabelecimento. Ele não é detalhe de cadastro: é a unidade em que a lei
 * opera. O RAT vem do CNAE dele, o enquadramento sindical da base territorial,
 * o feriado do município, e o S-1005 é por estabelecimento.
 */
export function Configuracoes() {
  const [abrindo, setAbrindo] = useState(false);
  const qc = useQueryClient();

  const empresas = useQuery({
    queryKey: ["cadastro", "empresas"],
    queryFn: () => trpc.cadastro.empresas.query(),
  });
  const locais = useQuery({
    queryKey: ["cadastro", "estabelecimentos"],
    queryFn: () => trpc.cadastro.estabelecimentos.query(),
  });

  const empresa = empresas.data?.[0];

  return (
    <Pagina
      secao="Configurações" titulo="Empresa e estabelecimentos"
      descricao="O estabelecimento é a unidade em que a lei opera: o RAT vem do CNAE dele, o sindicato da base territorial e o feriado do município."
      acoes={
        empresa ? (
          <button className="botao" data-variante="primario" onClick={() => setAbrindo(true)}>
            <Plus aria-hidden size={15} /> Novo estabelecimento
          </button>
        ) : undefined
      }
    >
      {empresas.isError && <Falha erro={empresas.error} tentarDeNovo={() => void empresas.refetch()} />}
      {empresas.isPending && <Carregando linhas={3} />}

      {empresa && (
        <Cartao titulo={empresa.razaoSocial}
                subtitulo={`CNPJ raiz ${fCnpj(empresa.cnpjRaiz)} · eSocial em ambiente ${empresa.ambienteESocial}`}>
          <dl className="eop-dados">
            <dt>Nome fantasia</dt><dd>{empresa.nomeFantasia ?? "sem registro"}</dd>
            <dt>Classificação tributária</dt>
            <dd className="num">{empresa.classificacaoTributaria ?? "sem registro"}</dd>
          </dl>
        </Cartao>
      )}

      <Cartao titulo="Estabelecimentos"
              subtitulo={locais.data?.length
                ? `${locais.data.length} cadastrados, ${locais.data.filter((l) => l.matriz).length} matriz`
                : "Nenhum cadastrado ainda"}>
        {locais.isPending && <Carregando linhas={3} nu />}
        {locais.isError && <Falha erro={locais.error} tentarDeNovo={() => void locais.refetch()} />}
        {locais.isSuccess && locais.data.length === 0 && (
          <EmptyState
            titulo="Sem estabelecimento, ninguém pode ser admitido"
            descricao="O vínculo exige um estabelecimento, e trocar depois significa refazer o S-2200 de todo mundo. Comece pela matriz."
            {...(empresa
              ? { acao: (
                  <button className="botao" data-variante="primario" onClick={() => setAbrindo(true)}>
                    Cadastrar a matriz
                  </button>
                ) }
              : {})}
          />
        )}
        {locais.isSuccess && locais.data.length > 0 && (
          <ul className="eop-locais">
            {locais.data.map((l) => (
              <li key={l.id}>
                <span aria-hidden className="eop-locais-icone">
                  <Building2 size={15} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="eop-locais-nome">
                    {l.matriz ? "Matriz" : "Filial"}
                    <span className="num"> · {l.tipoInscricao.toUpperCase()} {fCnpj(l.numeroInscricao)}</span>
                  </span>
                  <span className="eop-locais-sub">
                    <MapPin aria-hidden size={11} /> {l.uf} · IBGE {l.municipioIbge}
                    {" · "}CNAE {l.cnaePreponderante}
                    {l.timezone !== "America/Sao_Paulo" && ` · ${l.timezone}`}
                  </span>
                </span>
                <span className="pilula">
                  {l.pessoas} {l.pessoas === 1 ? "pessoa" : "pessoas"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Cartao>

      {abrindo && empresa && (
        <FormEstabelecimento
          empresaId={empresa.id}
          primeiro={(locais.data?.length ?? 0) === 0}
          aoFechar={() => setAbrindo(false)}
          aoCriar={() => { setAbrindo(false); void qc.invalidateQueries({ queryKey: ["cadastro"] }); }}
        />
      )}
    </Pagina>
  );
}

function FormEstabelecimento(
  { empresaId, primeiro, aoFechar, aoCriar }:
  { empresaId: string; primeiro: boolean; aoFechar: () => void; aoCriar: () => void },
) {
  const [f, setF] = useState({
    tipoInscricao: "cnpj" as "cnpj" | "caepf" | "cno",
    numeroInscricao: "", matriz: primeiro,
    cnaePreponderante: "", municipioIbge: "", uf: "SP",
    timezone: "America/Sao_Paulo",
  });
  const [erro, setErro] = useState<string | null>(null);

  const criar = useMutation({
    mutationFn: () => trpc.cadastro.criarEstabelecimento.mutate({ empresaId, ...f }),
    onSuccess: aoCriar,
    onError: (e: unknown) => setErro(e instanceof Error ? e.message : String(e)),
  });

  const campo = (k: keyof typeof f) => ({
    value: String(f[k]),
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setF((v) => ({ ...v, [k]: e.target.value })),
  });

  return (
    <div className="eop-modal-fundo" role="dialog" aria-modal="true"
         aria-label="Novo estabelecimento"
         onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="eop-modal">
        <header className="painel-topo">
          <h2 className="text-[0.9375rem] font-semibold">Novo estabelecimento</h2>
          <p className="mt-1 text-xs" style={{ color: "var(--faint-foreground)" }}>
            O CNAE define o RAT, e o município define o feriado que entra na apuração do ponto.
          </p>
        </header>
        <form className="painel-corpo space-y-3.5"
              onSubmit={(e) => { e.preventDefault(); setErro(null); criar.mutate(); }}>
          <div className="grid gap-3.5 sm:grid-cols-2">
            <label className="block">
              <span className="eop-rotulo">Tipo de inscrição</span>
              <select className="campo mt-1.5" {...campo("tipoInscricao")}>
                <option value="cnpj">CNPJ</option>
                <option value="caepf">CAEPF</option>
                <option value="cno">CNO</option>
              </select>
            </label>
            <label className="block">
              <span className="eop-rotulo">Número</span>
              <input className="campo mt-1.5" inputMode="numeric" {...campo("numeroInscricao")} />
            </label>
            <label className="block">
              <span className="eop-rotulo">CNAE preponderante</span>
              <input className="campo mt-1.5" inputMode="numeric" placeholder="7911200"
                     {...campo("cnaePreponderante")} />
            </label>
            <label className="block">
              <span className="eop-rotulo">Código IBGE do município</span>
              <input className="campo mt-1.5" inputMode="numeric" placeholder="3550308"
                     {...campo("municipioIbge")} />
            </label>
            <label className="block">
              <span className="eop-rotulo">UF</span>
              <select className="campo mt-1.5" {...campo("uf")}>
                {UFS.map((u) => <option key={u.sigla} value={u.sigla}>{u.sigla} · {u.nome}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="eop-rotulo">Fuso horário</span>
              <select className="campo mt-1.5" {...campo("timezone")}>
                {/* O Brasil tem 4 fusos, e o ponto se apura na hora local do
                    estabelecimento, nunca na do servidor. */}
                <option value="America/Sao_Paulo">Brasília (UTC-3)</option>
                <option value="America/Manaus">Manaus (UTC-4)</option>
                <option value="America/Rio_Branco">Acre (UTC-5)</option>
                <option value="America/Noronha">Fernando de Noronha (UTC-2)</option>
              </select>
            </label>
          </div>

          <label className="flex items-center gap-2">
            <input type="checkbox" checked={f.matriz}
                   onChange={(e) => setF((v) => ({ ...v, matriz: e.target.checked }))} />
            <span className="text-sm">É a matriz</span>
          </label>
          {f.matriz && !primeiro && (
            <p className="carimbo">marcar como matriz rebaixa a matriz atual para filial</p>
          )}

          {erro && (
            <p className="eop-erro" role="alert">
              <CircleAlert aria-hidden size={15} /><span>{erro}</span>
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="botao" data-variante="silencioso" onClick={aoFechar}>
              Cancelar
            </button>
            <button type="submit" className="botao" data-variante="primario"
                    disabled={criar.isPending}>
              {criar.isPending ? "Salvando" : "Cadastrar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
